import { IClinic, IInventory, IProduct } from "@interfaces";
import React, { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, FormInstance, UploadProps } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";
import { PATH_CLINIC } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";

import { useNotify, getApiErrorMessage } from "@utils";
import { useHistory } from "react-router";
import { SharedButton } from "@components";
import styled from "styled-components";

interface IProps {
  inventory?: IInventory;
}

const initValue: IInventory = {
  productId: "",
  expiredDate: "",
  stockAmount: 0,
  batchNumber: "",
};

export const InventoryForm = memo(({ inventory }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupInventory } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const inventorySchema = Yup.object().shape(YupInventory);
  const [data, setData] = useState<IInventory>(initValue);
  const [clinicForm] = Form.useForm();

  useEffect(() => {
    if (inventory) {
      setData({ ...data, ...inventory });
      formRef.current?.setFieldsValue({
        ...data,
        ...inventory,
      });
    }
  }, [inventory]);

  const onFinish = async (values: IClinic) => {
    try {
      dispatch(setLoading(true));

      await console.log(values);
      success(t("message.create.success"));
      history.push(PATH_CLINIC);
    } catch (err: any) {
      error(getApiErrorMessage(err, t("message.create.fail")));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await inventorySchema.validateSyncAt(field, { [field]: value });
    },
  };

  const goBack = () => history.goBack();

  const propsUpload: UploadProps = {
    name: "file",
    action: "https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188",
    headers: {
      authorization: "authorization-text",
    },
    onChange(info) {
      if (info.file.status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (info.file.status === "done") {
        success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === "error") {
        error(`${info.file.name} file upload failed.`);
      }
    },
  };
  return (
    <>
      <Form
        form={clinicForm}
        name="basic"
        ref={formRef}
        initialValues={data}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
      >
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.product_name")} name="productName" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.suk_code")} name="sukCode" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.stock_fill_amount")} name="stockAmount" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.expiry_date")} name="expiryDate" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.batch_number")} name="batchNumber" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}></Col>
        </Row>

        <Form.Item>
          <StyledFormAction>
            <SharedButton type="default" htmlType="button" size="middle" onClick={goBack}>
              {t("page.cancel")}
            </SharedButton>
            <SharedButton type="primary" htmlType="submit" size="middle">
              {t("page.create")}
            </SharedButton>
          </StyledFormAction>
        </Form.Item>
      </Form>
    </>
  );
});

const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
`;
